/**
 * AI 模型枚举
 */

// 品牌常量
export const APP_BRAND = {
  PRODUCT_NAME: "AgentDesk", // 产品名
  PRODUCT_SUBTITLE: "智能体任务工作台", // 产品副标题
  MODULE_ENTRY_NAME: "Agent 工作台", // 模块入口名
  SLOGAN: "让 AI 不只回答，也能规划、执行与留痕", // 产品标语
  META_DESCRIPTION:
    "AgentDesk 是面向 AI Agent 的对话、任务规划、工具执行与过程留痕工作台。", // SEO 页面描述
} as const;

export enum AIModel {
  /** DeepSeek 默认模型（纯文本） */
  DEEPSEEK = "deepseek-v4-flash",

  /** 通义千问视觉模型（图片） */
  QWEN_VL = "qwen-vl-plus",

  /** 通义千问长文本模型（文档） */
  QWEN_LONG = "qwen-long",
}

export enum AIProvider {
  DEEPSEEK = "deepseek",
  QWEN = "qwen",
}

export enum AIProviderErrorCode {
  QUOTA_EXHAUSTED = "QUOTA_EXHAUSTED",
  AUTH_FAILED = "AUTH_FAILED",
  SERVICE_UNAVAILABLE = "SERVICE_UNAVAILABLE",
}

export const AI_MODEL_FALLBACK = {
  DEEPSEEK: `${AIModel.DEEPSEEK}-fallback`,
} as const;

export type AIFallbackModel =
  (typeof AI_MODEL_FALLBACK)[keyof typeof AI_MODEL_FALLBACK];

// AI 模型速率限制配置信息
export const CHAT_RATE_LIMIT_CONFIG = {
  ANONYMOUS_HOURLY_LIMIT: 1, // 每小时匿名用户请求限制
  ANONYMOUS_WINDOW_SECONDS: 60 * 60, // 速率限制窗口秒数
} as const;

export enum AIGatewayProvider {
  DIRECT = "direct",
  CLOUDFLARE = "cloudflare",
  VERCEL = "vercel",
}

export const AI_MODEL_PROVIDER_MAP: Record<AIModel, AIProvider> = {
  [AIModel.DEEPSEEK]: AIProvider.DEEPSEEK,
  [AIModel.QWEN_VL]: AIProvider.QWEN,
  [AIModel.QWEN_LONG]: AIProvider.QWEN,
};

export const SESSION_TITLE_CONFIG = {
  DEFAULT_TITLE: "新对话",
  MAX_GENERATED_LENGTH: 24,
} as const;

/**
 * AI 模型描述信息
 */
export const AI_MODEL_DESCRIPTIONS: Record<AIModel, string> = {
  [AIModel.DEEPSEEK]: "DeepSeek 默认模型（纯文本对话）",
  [AIModel.QWEN_VL]: "通义千问视觉模型（图片分析）",
  [AIModel.QWEN_LONG]: "通义千问长文本模型（文档处理）",
};

/**
 * 用户账号角色枚举
 */
export enum UserRole {
  USER = "user",
  ADMIN = "admin",
}

export enum PromptExperimentStatus {
  RUNNING = "running",
  PAUSED = "paused",
}

export const PROMPT_EXPERIMENT_STATUS_LABELS: Record<
  PromptExperimentStatus,
  string
> = {
  [PromptExperimentStatus.RUNNING]: "运行中",
  [PromptExperimentStatus.PAUSED]: "已暂停",
};

/**
 * 用户菜单命令
 */
export enum ApiErrorCode {
  BAD_REQUEST = "BAD_REQUEST",
  UNAUTHORIZED = "UNAUTHORIZED",
  FORBIDDEN = "FORBIDDEN",
  NOT_FOUND = "NOT_FOUND",
  CONFLICT = "CONFLICT",
  VALIDATION_ERROR = "VALIDATION_ERROR",
  RATE_LIMITED = "RATE_LIMITED",
  INTERNAL_ERROR = "INTERNAL_ERROR",
  SERVICE_UNAVAILABLE = "SERVICE_UNAVAILABLE",
}

export enum AuthMenuCommand {
  PROFILE = "profile",
  LOGOUT = "logout",
}

/**
 * AI 工具风险等级
 */
export enum ToolRiskLevel {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
}

/**
 * AI 工具执行状态
 */
export enum ToolExecutionStatus {
  PENDING = "pending",
  RUNNING = "running",
  SUCCESS = "success",
  FAILED = "failed",
  SKIPPED = "skipped",
}

/**
 * AI 工具权限动作
 */
export enum ToolPermissionAction {
  READ = "read",
  WRITE = "write",
  DELETE = "delete",
  NETWORK = "network",
  ADMIN = "admin",
}

/**
 * AI 聊天工具模式
 */
export enum ChatToolMode {
  NONE = "none",
  AUTO = "auto",
  WEB = "web",
  PROJECT = "project",
  MEMORY = "memory",
  ALL = "all",
}

export enum MemoryKind {
  PROFILE = "profile",
  PREFERENCE = "preference",
  FACT = "fact",
  MESSAGE = "message",
}

export const MEMORY_CONTENT_LIMITS = {
  MANUAL_MIN_CHARS: 1,
  AUTO_MIN_CHARS: 8,
  MAX_CHARS: 1200,
} as const;

export const MEMORY_KIND_VALUES = [
  MemoryKind.PROFILE,
  MemoryKind.PREFERENCE,
  MemoryKind.FACT,
  MemoryKind.MESSAGE,
] as const;

export const MEMORY_KIND_LABELS: Record<MemoryKind, string> = {
  [MemoryKind.PROFILE]: "画像",
  [MemoryKind.PREFERENCE]: "偏好",
  [MemoryKind.FACT]: "事实",
  [MemoryKind.MESSAGE]: "消息",
} as const;

export const MEMORY_KIND_TAG_TYPES: Record<
  MemoryKind,
  "primary" | "success" | "warning" | "info"
> = {
  [MemoryKind.PROFILE]: "primary",
  [MemoryKind.PREFERENCE]: "success",
  [MemoryKind.FACT]: "warning",
  [MemoryKind.MESSAGE]: "info",
} as const;

export enum FeedbackType {
  LIKE = "like",
  DISLIKE = "dislike",
}

export const FEEDBACK_TYPE_VALUES = [
  FeedbackType.LIKE,
  FeedbackType.DISLIKE,
] as const;

export enum UsageLogSource {
  PROVIDER = "provider",
  ESTIMATED = "estimated",
  FALLBACK = "fallback",
}

export const USAGE_LOG_SOURCE_VALUES = [
  UsageLogSource.PROVIDER,
  UsageLogSource.ESTIMATED,
  UsageLogSource.FALLBACK,
] as const;

/**
 * Agent 任务状态
 */
export enum AgentTaskStatus {
  CREATED = "created",
  PLANNING = "planning",
  EXECUTING = "executing",
  COMPLETED = "completed",
  FAILED = "failed",
  CANCELLED = "cancelled",
}

/**
 * Agent 步骤状态
 */
export enum AgentStepStatus {
  PENDING = "pending",
  RUNNING = "running",
  SUCCESS = "success",
  FAILED = "failed",
  SKIPPED = "skipped",
}

/**
 * Agent 目标类型
 */
export enum AgentGoalType {
  QUESTION = "question",
  CODING = "coding",
  RESEARCH = "research",
  FILE_ANALYSIS = "file_analysis",
  LONG_RUNNING_TASK = "long_running_task",
}

export const AGENT_TASK_STATUS_VALUES = Object.values(AgentTaskStatus);
export const AGENT_STEP_STATUS_VALUES = Object.values(AgentStepStatus);
export const AGENT_GOAL_TYPE_VALUES = Object.values(AgentGoalType);

/**
 * 用户账号角色列表
 */
export const USER_ROLE_VALUES = [UserRole.USER, UserRole.ADMIN] as const;

/**
 * 用户账号角色显示文本映射
 */
export const USER_ROLE_DISPLAY_TEXT: Record<UserRole, string> = {
  [UserRole.USER]: "普通用户",
  [UserRole.ADMIN]: "管理员",
};

/**
 * 判断是否为有效的用户账号角色
 */
export const isUserRole = (role: unknown): role is UserRole =>
  typeof role === "string" && USER_ROLE_VALUES.includes(role as UserRole);

/**
 * 图片文件类型
 */
export const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp"];

/**
 * 文档文件类型
 */
export const DOCUMENT_EXTENSIONS = [
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "ppt",
  "pptx",
];

/**
 * 根据文件扩展名判断文件类型
 * @param extension 文件扩展名（不含点）
 */
export function getFileType(
  extension: string,
): "image" | "document" | "unknown" {
  const ext = extension.toLowerCase();
  if (IMAGE_EXTENSIONS.includes(ext)) {
    return "image";
  }
  if (DOCUMENT_EXTENSIONS.includes(ext)) {
    return "document";
  }
  return "unknown";
}

/**
 * 根据文件类型选择 AI 模型
 */
export function getModelByFileType(
  fileType: "image" | "document" | "unknown",
): AIModel {
  switch (fileType) {
    case "image":
      return AIModel.QWEN_VL;
    case "document":
      return AIModel.QWEN_LONG;
    default:
      return AIModel.DEEPSEEK;
  }
}

/**
 * 获取文件的 MIME 类型
 */
export function getMimeType(extension: string): string {
  const mimeTypes: Record<string, string> = {
    // 图片
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    gif: "image/gif",
    webp: "image/webp",
    // 文档
    pdf: "application/pdf",
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xls: "application/vnd.ms-excel",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ppt: "application/vnd.ms-powerpoint",
    pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  };
  return mimeTypes[extension.toLowerCase()] || "application/octet-stream";
}

/**
 * 聊天消息角色枚举
 */
export enum ChatRole {
  USER = "user",
  ASSISTANT = "assistant",
  SYSTEM = "system",
}

/**
 * 角色显示文本映射
 */
export const ROLE_DISPLAY_TEXT: Record<ChatRole, string> = {
  [ChatRole.USER]: "👤 用户",
  [ChatRole.ASSISTANT]: "🤖 AI",
  [ChatRole.SYSTEM]: "⚙️ 系统",
};

/**
 * 角色样式类名映射
 */
export const ROLE_CLASS_MAP: Record<ChatRole, string> = {
  [ChatRole.USER]: "user",
  [ChatRole.ASSISTANT]: "assistant",
  [ChatRole.SYSTEM]: "system",
};

/**
 * 文件存储提供商枚举
 * 统一管理所有存储服务类型，便于切换和维护
 */
export enum StorageProvider {
  /** 阿里云 OSS */
  ALIYUN = "aliyun",

  /** Cloudflare R2 */
  R2 = "r2",

  /** 本地文件系统（仅开发环境） */
  LOCAL = "local",
}

/**
 * 存储提供商描述信息
 */
export const STORAGE_PROVIDER_DESCRIPTIONS: Record<StorageProvider, string> = {
  [StorageProvider.ALIYUN]: "阿里云对象存储服务",
  [StorageProvider.R2]: "Cloudflare R2 对象存储",
  [StorageProvider.LOCAL]: "本地文件系统（开发环境）",
};

/**
 * 统一管理所有本地存储的 key，避免硬编码字符串
 */
export enum StorageKey {
  /** 聊天消息历史 */
  CHAT_MESSAGES = "chat-messages",

  /** 会话历史记录 */
  CHAT_SESSIONS = "chat-sessions",

  /** 当前会话 ID（页面刷新后保持不变） */
  CURRENT_SESSION_ID = "chat-current-session-id",

  /** 匿名用户 ID（未登录时的固定用户标识） */
  ANONYMOUS_USER_ID = "chat-anonymous-user-id", //  这个是 cookie 使用

  /** 用户偏好设置 */
  USER_PREFERENCES = "chat-preferences",

  /** 主题设置 */
  THEME_SETTING = "chat-theme",

  /** 语言设置 */
  LANGUAGE_SETTING = "chat-language",

  /** 会话搜索模式状态 */
  SESSION_SEARCH_MODE = "session-search-mode",

  /** 会话搜索关键词 */
  SESSION_SEARCH_KEYWORD = "session-search-keyword",

  /** 会话列表滚动位置 */
  SESSION_LIST_SCROLL_TOP = "session-list-scroll-top",

  /**  AI 会话历史记录*/
  AI_CHAR_MEMORY_ENABLED = "ai_chat_memory_enabled",
}

/**
 * 存储键的描述信息（用于调试和文档）
 */
export const STORAGE_KEY_DESCRIPTIONS: Record<StorageKey, string> = {
  [StorageKey.CHAT_MESSAGES]: "聊天消息历史记录",
  [StorageKey.CHAT_SESSIONS]: "会话历史列表",
  [StorageKey.CURRENT_SESSION_ID]: "当前活跃的会话 ID",
  [StorageKey.ANONYMOUS_USER_ID]: "匿名用户唯一标识",
  [StorageKey.USER_PREFERENCES]: "用户个性化偏好设置",
  [StorageKey.THEME_SETTING]: "界面主题配置",
  [StorageKey.LANGUAGE_SETTING]: "界面语言设置",
  [StorageKey.SESSION_SEARCH_MODE]: "会话搜索模式状态（是否处于搜索模式）",
  [StorageKey.SESSION_SEARCH_KEYWORD]: "会话搜索关键词",
  [StorageKey.SESSION_LIST_SCROLL_TOP]: "会话列表滚动位置",
  [StorageKey.AI_CHAR_MEMORY_ENABLED]: "AI 会话历史记录",
};

/**
 * 获取存储键的工具函数
 * @param key - 存储键枚举
 * @returns 存储键字符串
 *
 * @example
 * import { StorageKey, getStorageKey } from '~/enums'
 * localStorage.getItem(getStorageKey(StorageKey.CHAT_MESSAGES))
 */
export function getStorageKey(key: StorageKey): string {
  return key;
}

/**
 * 清除指定存储键的数据
 * @param key - 存储键枚举
 */
export function clearStorage(key: StorageKey): void {
  if (typeof window !== "undefined") {
    localStorage.removeItem(key);
    console.log(`🗑️ 已清除存储: ${STORAGE_KEY_DESCRIPTIONS[key]} (${key})`);
  }
}

/**
 * 清除所有应用相关的存储
 */
export function clearAllAppStorage(): void {
  if (typeof window !== "undefined") {
    Object.values(StorageKey).forEach((key) => {
      localStorage.removeItem(key);
    });
    console.log("🗑️ 已清除所有应用存储");
  }
}
